import { nonEmpty, VariantAttribute } from "@swo/shared";
import { array, nonEmptyArray, option } from "fp-ts";
import { NonEmptyArray } from "fp-ts/NonEmptyArray";
import { pipe } from "fp-ts/lib/function";
import * as NEA from "fp-ts/lib/NonEmptyArray";
import * as O from "fp-ts/lib/Option";
import { attributeArrayUniq } from "./attributes";
import { PDPSwarovskiProductFrontend } from "./types";

export type PDPProductMultiselects =
  | NonEmptyArray<{
      headline: string;
      name: string;
      attributes: NonEmptyArray<VariantAttribute>;
    }>
  | undefined;

export function calculateMultiselects(
  selectedProduct: Pick<PDPSwarovskiProductFrontend, "variants">,
): PDPProductMultiselects {
  function getVariantUniqueAttributes(): Array<VariantAttribute> {
    // Look for variants with the longest list of unique attributes
    // to account for some mismatches between variants
    // ie. maybe one of them is broken and has 1 attribute less
    const [first, ...rest] = pipe(
      selectedProduct.variants,
      NEA.map(v => v.attributes.unique || []),
    );

    let candidate = first;
    rest.forEach(r => {
      if (r.length > candidate.length) {
        candidate = r;
      }
    });

    return candidate;
  }

  return pipe(
    getVariantUniqueAttributes(),
    NEA.fromArray,
    O.fold(
      () => undefined,
      variantUniqueAttributes =>
        pipe(
          variantUniqueAttributes,
          nonEmptyArray.mapWithIndex((idx, variantAttribute) => {
            const uniqueAttributes = pipe(
              selectedProduct.variants.flatMap(v => {
                const unique = v.attributes.unique;
                if (unique) {
                  const u = unique[idx];

                  if (u && u.name === variantAttribute.name) {
                    return [u];
                  }

                  return [];
                } else {
                  return [];
                }
              }),
              attributeArrayUniq,
              nonEmptyArray.fromArray,
              option.getOrElseW(() => {
                throw Error("Invalid variant attributes.");
              }),
            );

            return {
              headline: variantAttribute.label,
              name: variantAttribute.name,
              attributes: uniqueAttributes,
            };
          }),
        ),
    ),
  );
}
